import { ENEMIES } from "../config/enemies";
import { towerStatsForInstance, TOWERS } from "../config/towers";
import type { DamageType, EnemyInstance, TowerInstance } from "../domain/types";
import { selectTargets } from "./targeting";
import { beginDying, isDamageable } from "./enemy-lifecycle";

export interface CombatHit {
  readonly enemyId: string;
  readonly damage: number;
  readonly killed: boolean;
  readonly chainIndex: number;
  readonly type: DamageType;
}

export class CombatManager {
  attack(
    tower: TowerInstance,
    enemies: readonly EnemyInstance[],
    allTowers: readonly TowerInstance[] = [],
  ): CombatHit[] {
    const definition = TOWERS[tower.definitionId];
    if (!definition) return [];
    const stats = towerStatsForInstance(definition, tower);
    const canSeeStealth =
      tower.level >= 3 ||
      definition.tags.includes("reveal") ||
      allTowers.some((other) => {
        const otherDefinition = TOWERS[other.definitionId];
        if (!otherDefinition?.tags.includes("reveal")) return false;
        const revealStats = towerStatsForInstance(otherDefinition, other);
        const revealRadius = revealStats.range + (other.revealAuraBonus ?? 0);
        const dx = other.origin.x - tower.origin.x;
        const dy = other.origin.y - tower.origin.y;
        return dx * dx + dy * dy <= revealRadius * revealRadius;
      });
    const eligibleEnemies = enemies.filter((enemy) => {
        const enemyDefinition = ENEMIES[enemy.definitionId];
        if (!enemyDefinition) return false;
        const movementClass = enemyDefinition.movementClass ?? "ground";
        if (!definition.validTargetClasses.includes(movementClass)) return false;
        return !enemyDefinition.stealth || canSeeStealth;
      }),
      targets = selectTargets(
        eligibleEnemies,
        tower.origin,
        stats.range,
        tower.targetingMode,
        stats.attack.simultaneousTargets,
      );
    const hits: CombatHit[] = [];
    for (const target of targets) {
      if (stats.attack.kind === "splash")
        hits.push(
          ...this.splash(
            target,
            enemies,
            tower,
            stats.attack.damage,
            stats.attack.damageType,
            stats.attack.splashRadius,
            stats.attack.splashFalloff,
          ),
        );
      else if (stats.attack.kind === "instant-chain")
        hits.push(
          ...this.chain(
            target,
            enemies,
            tower,
            stats.attack.damage,
            stats.attack.damageType,
            stats.attack.chainTargets,
            stats.attack.chainLoss,
            stats.attack.splashRadius,
          ),
        );
      else
        hits.push(this.applyDamage(target, tower, stats.attack.damage, stats.attack.damageType, 0));
      if (stats.attack.armorBreak > 0)
        for (const hit of hits) {
          const enemy = enemies.find((entry) => entry.id === hit.enemyId);
          if (enemy && isDamageable(enemy))
            enemy.statuses.push({
              id: `${tower.id}-armor-${enemy.id}`,
              kind: "armor-break",
              magnitude: stats.attack.armorBreak,
              remainingMs: stats.attack.armorBreakDurationMs,
            });
        }
    }
    return this.uniqueHits(hits);
  }

  updateStatuses(enemy: EnemyInstance, deltaMs: number): void {
    for (const status of enemy.statuses) status.remainingMs -= deltaMs;
    enemy.statuses.splice(
      0,
      enemy.statuses.length,
      ...enemy.statuses.filter((status) => status.remainingMs > 0),
    );
    const definition = ENEMIES[enemy.definitionId];
    enemy.effectiveArmor = Math.max(
      0,
      (definition?.armor ?? 0) -
        enemy.statuses
          .filter((status) => status.kind === "armor-break")
          .reduce((sum, status) => sum + status.magnitude, 0),
    );
  }

  applyDamage(
    enemy: EnemyInstance,
    _tower: TowerInstance,
    raw: number,
    type: DamageType,
    chainIndex: number,
  ): CombatHit {
    if (!isDamageable(enemy))
      return { enemyId: enemy.id, damage: 0, killed: false, chainIndex, type };
    const definition = ENEMIES[enemy.definitionId],
      resistance = definition?.resistances[type] ?? 0,
      armorReduction = type === "physical" || type === "explosive" ? enemy.effectiveArmor : 0;
    const calculated = Math.max(1, Math.round(raw * (1 - resistance) * (1 - armorReduction)));
    let remaining = calculated;
    if ((enemy.shieldHealth ?? 0) > 0) {
      const absorbed = Math.min(enemy.shieldHealth ?? 0, remaining);
      enemy.shieldHealth = Math.max(0, (enemy.shieldHealth ?? 0) - absorbed);
      remaining -= absorbed;
    }
    const healthDamage = Math.min(enemy.health, remaining);
    enemy.health = Math.max(0, enemy.health - healthDamage);
    enemy.lastDamageType = type;
    const killed = enemy.health === 0 && definition ? beginDying(enemy, definition) : false;
    return { enemyId: enemy.id, damage: calculated, killed, chainIndex, type };
  }
  private splash(
    target: EnemyInstance,
    enemies: readonly EnemyInstance[],
    tower: TowerInstance,
    damage: number,
    type: DamageType,
    radius: number,
    falloff: number,
  ): CombatHit[] {
    return enemies
      .filter(
        (enemy) =>
          isDamageable(enemy) &&
          (enemy.position.x - target.position.x) ** 2 +
            (enemy.position.y - target.position.y) ** 2 <=
            radius ** 2,
      )
      .sort((a, b) => a.id.localeCompare(b.id))
      .map((enemy) =>
        this.applyDamage(enemy, tower, enemy.id === target.id ? damage : damage * falloff, type, 0),
      );
  }
  private chain(
    target: EnemyInstance,
    enemies: readonly EnemyInstance[],
    tower: TowerInstance,
    damage: number,
    type: DamageType,
    chainTargets: number,
    loss: number,
    radius: number,
  ): CombatHit[] {
    const hits = [this.applyDamage(target, tower, damage, type, 0)],
      used = new Set([target.id]);
    let current = target;
    for (let index = 1; index <= chainTargets; index += 1) {
      const next = enemies
        .filter(
          (enemy) =>
            isDamageable(enemy) &&
            !used.has(enemy.id) &&
            (enemy.position.x - current.position.x) ** 2 +
              (enemy.position.y - current.position.y) ** 2 <=
              radius ** 2,
        )
        .sort(
          (a, b) =>
            (a.position.x - current.position.x) ** 2 +
              (a.position.y - current.position.y) ** 2 -
              ((b.position.x - current.position.x) ** 2 +
                (b.position.y - current.position.y) ** 2) || a.id.localeCompare(b.id),
        )[0];
      if (!next) break;
      used.add(next.id);
      hits.push(this.applyDamage(next, tower, damage * loss ** index, type, index));
      current = next;
    }
    return hits;
  }
  private uniqueHits(hits: readonly CombatHit[]): CombatHit[] {
    const totals = new Map<string, CombatHit>();
    for (const hit of hits) {
      const previous = totals.get(hit.enemyId);
      totals.set(
        hit.enemyId,
        previous
          ? {
              enemyId: hit.enemyId,
              damage: previous.damage + hit.damage,
              killed: previous.killed || hit.killed,
              chainIndex: Math.min(previous.chainIndex, hit.chainIndex),
              type: previous.type,
            }
          : hit,
      );
    }
    return [...totals.values()];
  }
}
